/**
 * All logic behind "Create from Library": the library query for one form category, the two-step
 * wizard (pick flows → choose who to assign), and the copy-into-project call.
 *
 * Platform-agnostic on purpose: written to be consumed by `packages/mobile` as well as
 * `packages/web`, so it returns state, handlers and option lists only — never markup. The platform
 * passes in how to read the current selection of its own list.
 *
 * NOTE for the mobile port: this hook is the one that dispatches `getAllUsersInSpace` from
 * `@Actions/projectUsers`, which — unlike every action mobile already consumes — imports
 * `../services/axiosWrapper` by relative path instead of `@AxiosWrapper`. That relative import
 * resolves to the web axios instance (and through it the web store) on mobile too. Fix that import,
 * or inject the space-users loader the way `libraryServiceUrl` is injected, before wiring this hook
 * into a mobile screen.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "@i18n";
import { useDispatch, useSelector } from "react-redux";
import { formFlowController } from "@Actions/Forms";
import { getAllUsersInSpace } from "@Actions/projectUsers";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import {
  buildFormFlowLibraryQuery,
  DEFAULT_USER_SELECTION_MODE,
  FORM_FLOW_LIBRARY_STEPS,
  getUserSelectionModeOptions,
  toFlowUserFromNameParts,
  USER_SELECTION_MODES,
} from "./formFlow.constants";
import type { AddFromLibraryState, FormFlowRow } from "./formFlow.types";

interface UseFormFlowLibraryParams {
  projectId?: string;
  /** Form category whose library entries are listed. */
  resourceType: string;
  /**
   * Endpoint the library list pages against, supplied by the platform. Kept as a param rather
   * than imported here: `@Services/dataManager` builds its URLs at module-load time, which is
   * unsafe before the store holds a backend URL (notably on mobile).
   */
  libraryServiceUrl?: string;
  /** Current wizard state, owned by the caller so the list screen can open/close it. */
  addFromLibrary: AddFromLibraryState;
  setAddFromLibrary: (updater: (prev: AddFromLibraryState) => AddFromLibraryState) => void;
  /** Closes the wizard. */
  handleCancelibrary: () => void;
  /** Re-fetch the project's flows after a successful copy. */
  refreshGrid: () => void;
  /** Current selection of the library list, as row data. */
  getSelectedRows?: () => FormFlowRow[];
}

function useFormFlowLibrary({
  projectId,
  resourceType,
  libraryServiceUrl,
  addFromLibrary,
  setAddFromLibrary,
  handleCancelibrary,
  refreshGrid,
  getSelectedRows,
}: UseFormFlowLibraryParams) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { spaceUsers, userDetails } = useSelector((state: RootState) => state);

  // `undefined` until the Space picker fires — there is no id for "no Space picked". The id itself
  // is typed at the picker callback, not at the request.
  const [spaceId, setSpaceId] = useState<ObjectIdString | undefined>(undefined);
  const [nextBtnState, setNextBtnState] = useState(false);
  const [selectedFlowFromLibrary, setSelectedFlowFromLibrary] = useState<any[]>([]);
  const [userSelectionMode, setUserSelectionMode] = useState<string>(DEFAULT_USER_SELECTION_MODE);

  const getQuery = useCallback(() => buildFormFlowLibraryQuery(resourceType), [resourceType]);
  const [customQuery, setCustomQuery] = useState<any>(getQuery());

  /** Remote source the library list pages against. */
  const libraryService = libraryServiceUrl;

  useEffect(() => {
    setCustomQuery(getQuery());
  }, [getQuery]);

  // The assign-users step offers the users of the chosen space, so load them once it is picked.
  useEffect(() => {
    if (spaceId) {
      dispatch(getAllUsersInSpace({ spaceId }));
    }
  }, [spaceId, dispatch]);

  /** Copies the picked library flows into the project, assigning the chosen users. */
  const handleCreateFromLibrary = async () => {
    // `spaceId` is a required 24-hex id on formFlowController's addFromLibrary schema; step 1's
    // Next button already blocks reaching this step without a Space, so nothing to send here.
    if (!spaceId) {
      return;
    }
    if (selectedFlowFromLibrary && selectedFlowFromLibrary.length) {
      let usersData = spaceUsers.map((user: any) => toFlowUserFromNameParts(user));

      if (userSelectionMode == USER_SELECTION_MODES.CURRENT_USER) {
        usersData = [toFlowUserFromNameParts(userDetails as any)];
      }
      const formFlowIds = selectedFlowFromLibrary.map((obj: any) => obj._id);

      const res = await formFlowController({
        action: "addFromLibrary",
        formFlowIds,
        // Ids reaching the client are already validated ObjectIds; the request schemas brand them.
        usersData: usersData as Array<{ id: ObjectIdString; name: string }>,
        spaceId,
        projectId: projectId as ObjectIdString,
      });
      let toastContent: any = "";
      if (res.data.status) {
        toastContent = {
          content: t("Selected Form Flows have been added successfully"),
          type: TOAST_TYPE_SUCCESS,
        };
        refreshGrid();
        handleCancelibrary();
      } else {
        toastContent = {
          content: res.data.errorMessage,
          type: TOAST_TYPE_ERROR,
        };
      }
      dispatch(showToastNotification(toastContent));
    }
  };

  /** Locks in the picked flows and moves on to the assign-users step. */
  const handleNextClick = () => {
    setSelectedFlowFromLibrary(getSelectedRows?.() ?? []);
    setAddFromLibrary((prev) => ({ ...prev, step: FORM_FLOW_LIBRARY_STEPS.ASSIGN_USERS }));
  };

  const handleBackToSelection = () => {
    setAddFromLibrary((prev) => ({ ...prev, step: FORM_FLOW_LIBRARY_STEPS.SELECT_FLOWS }));
  };

  /** Adapter for web radios that emit a `{ target: { value } }` change event. */
  const handleUserSelectionModeChange = (args: { target: { value: string } }) => {
    setUserSelectionMode(args.target.value);
  };

  const isSelectionStep = addFromLibrary.step == FORM_FLOW_LIBRARY_STEPS.SELECT_FLOWS;
  /** Next needs both a picked flow and a target space (the flow's users come from it). */
  const isNextDisabled = !nextBtnState || !spaceId;

  const userSelectionModeOptions = useMemo(() => getUserSelectionModeOptions(), []);

  return {
    // list wiring
    customQuery,
    libraryService,

    // wizard
    isSelectionStep,
    handleNextClick,
    handleBackToSelection,
    isNextDisabled,

    // space + selection
    spaceId,
    setSpaceId,
    nextBtnState,
    setNextBtnState,
    selectedFlowFromLibrary,

    // user assignment
    userSelectionMode,
    setUserSelectionMode,
    handleUserSelectionModeChange,
    userSelectionModeOptions,

    // persistence
    handleCreateFromLibrary,
  };
}

export default useFormFlowLibrary;
